import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { orgApi, apiErrorMessage } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { Spinner } from '../components/ui';
import CreateOrgScreen from '../components/CreateOrgScreen';
import { awaitsPrepgen, standingChanged } from '../utils/institute';

const OrgContext = createContext(null);
const OrgReloadContext = createContext(() => {});

// How often a console that is waiting on Prepgen asks again.
const STANDING_RECHECK_MS = 60 * 1000;

/** The institute this user owns, or null when they own none yet. */
async function fetchOwnedOrg(user) {
  const res = await orgApi.getUserOrganizations(user.uid);
  const orgs = res.data || [];
  const owned = orgs.find((o) => o.role === 'owner' && o.status === 'active');
  if (!owned) return null;

  // The list endpoint returns a summary. Branding and feature flags live
  // on the full document, so fetch that too; if it fails the summary is
  // still enough to run the console.
  let full = {};
  try {
    full = (await orgApi.getOrgById(owned.id)).data || {};
  } catch (_) {
    full = {};
  }
  return { ...full, ...owned, id: owned.id, metadata: full.metadata || {}, features: full.features };
}

export function OrgProvider({ children }) {
  const [org, setOrg] = useState(null);
  const [needsOrg, setNeedsOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const user = getCurrentUser();
    if (!user) {
      setLoading(false);
      setError('Not authenticated');
      return;
    }
    setLoading(true);
    setError(null);
    setNeedsOrg(false);
    try {
      const owned = await fetchOwnedOrg(user);
      if (!owned) {
        setNeedsOrg(true);
        return;
      }
      setOrg(owned);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load your organization'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Activation, suspension and the student cap are changed by Prepgen, outside
  // this console. Without this an owner who kept the tab open would go on
  // seeing "being reviewed" after their institute was activated, until they
  // happened to reload. So while the institute is waiting, ask again when the
  // tab comes back into view and once a minute. It is deliberately quiet: no
  // spinner, and a failed check leaves the page as it is.
  useEffect(() => {
    if (!awaitsPrepgen(org)) return undefined;
    let cancelled = false;
    const recheck = async () => {
      if (document.visibilityState !== 'visible') return;
      const user = getCurrentUser();
      if (!user) return;
      try {
        const latest = await fetchOwnedOrg(user);
        if (!cancelled && latest && standingChanged(org, latest)) setOrg(latest);
      } catch (_) {
        // Keep what is on screen; the next check tries again.
      }
    };
    const timer = setInterval(recheck, STANDING_RECHECK_MS);
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [org]);

  if (loading && !org) return <div className="page-loading"><Spinner /> Loading your institute…</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (needsOrg) return <CreateOrgScreen onCreated={load} />;

  return (
    <OrgReloadContext.Provider value={load}>
      <OrgContext.Provider value={org}>{children}</OrgContext.Provider>
    </OrgReloadContext.Provider>
  );
}

export function useOrg() {
  return useContext(OrgContext);
}

/** Re-fetch the organization after something on it changes, such as branding. */
export function useReloadOrg() {
  return useContext(OrgReloadContext);
}
